/**
 * Derived data. Computed ONCE per dataset and shared by every page.
 *
 * The bundle arrives as flat fact tables; almost every visual needs the same
 * per-person rollup, so building it repeatedly inside components is both slow and
 * a good way to let two pages disagree about the same number.
 */
import { isNoClaim } from './noClaim'
import type {
  CompositionRow,
  Dataset,
  EmployeeRow,
  TouchMonthRow,
  WorklistRow,
} from './types'
import type { Coverage, GrainKey } from './coverage'
import {
  divide,
  touchBase,
  worklistAge,
  type CollectionBasis,
  type WorklistAge,
} from '@/measures/core'
import {
  collectionRateOf,
  collectionWindow,
  windowCollection,
  type CollectionSeries,
  type CollectionWindow,
} from '@/measures/collection'
import { foldToPeople, MIN_COHORT } from '@/measures/peer'
import { int } from '@/format'
import { titlePlural, type RankFacts } from '@/measures/rankText'
import { isLowVolume, scorecard, type PersonStats, type Scorecard } from '@/measures/scorecard'
import { fullMonth, rangeVolume, usualHours, volumeBars, type VolumeBar } from '@/measures/minimumVolume'
import { isNoClaimMonth } from './hydrate'

/** Everything the app knows about one person, in one object. */
export interface Person {
  employee: string
  supervisor: string | null
  /** Reporting line, direct supervisor first, to the top. See `EmployeeRow`. */
  supervisors: string[]
  /** In step with `supervisors`: each link's own job title, or null. See `EmployeeRow`. */
  supervisorTitles: (string | null)[]
  /** The direct supervisor is outside the caller's access. See `EmployeeRow`. */
  supervisorOutsideScope: boolean
  department: string
  jobTitle: string
  shore: 'On' | 'Off' | null
  /**
   * `DimEmployee.PaylocityId` off the person's latest record: the ID other systems know
   * them by (review #373). Null where the bundle has none.
   */
  paylocityId: string | null
  /** Paylocity vendor; 'Direct' for FCC payroll. Person attribute, filtered exactly. */
  vendor: string
  /** Paylocity work location. Person attribute, filtered exactly. */
  officeLocation: string
  management: boolean
  terminated: boolean
  /** Paylocity termination date, 'YYYY-MM-DD'; null while the person is on the books. */
  terminationDate: string | null
  tenureYears: number | null
  /** Hire date, 'YYYY-MM-DD', or null where neither it nor tenure is known. See `hireDateOf`. */
  hireDate: string | null
  /**
   * The job title's minimum billing volume for each of the person's months that has one
   * (measures/minimumVolume.ts): what the month scores test against. Set by `derive`.
   */
  volumeBars?: Readonly<Record<string, VolumeBar>>

  /**
   * The claim count every claim-based figure divides by: `standardClaims`, or
   * `weightedClaims` when the Claims switch is on Weighted (data/weights.ts).
   */
  claims: number
  /** BILLED claims, always. Closed % divides by this whatever the switch says. */
  standardClaims: number
  /**
   * Billed claims under the claim rules (docs/RULES.md): `standardClaims` plus each
   * month's Σ(weight − 1). Null when weights were not supplied to `derive`.
   */
  weightedClaims: number | null
  claimLabor: number
  /** The part of `claimLabor` that landed on no claim. See data/types.ts. */
  unattributedLabor: number
  /** Whole-period FactPayroll pay. What the payroll band filters on. See `labor`. */
  payroll: number
  /** Pay and hours for the RANGE, as the Employee view shows them. See `PersonLabor`. */
  labor: PersonLabor
  collectedPct: number | null
  /**
   * The two sums behind `collectedPct`: resolved charge and total charge over the
   * range's collection WINDOW (`Derived.collectionWindow`), not the range's months.
   * What every pooled collection rate (team, department, org) adds up — never the
   * rate itself. Null where the person has no charge in the window.
   */
  collection: CollectionBasis | null
  /**
   * The bundle's bill month → [charge, resolved] series the window is pooled from —
   * for anything that needs another window, such as one month's. Absent on a bundle
   * built before 2026-10-05.
   */
  collectionMonths?: CollectionSeries

  costPerClaim: number | null
  resolvedClaims: number
  closedClaimPct: number | null
  /**
   * Rule-of-record no-touch claims and their base, the three touch buckets summed.
   * The base is NOT `resolvedClaims` — a different date basis. See measures/core.ts.
   */
  noTouch: number
  touchBase: number
  zeroTouchShare: number | null
  touchesPerClaim: number | null
  billedPerMonth: number | null

  openClaims: number
  /** Open at the worklist snapshot and not touched by them in its month. Not "never worked". */
  notWorkedThisMonth: number
  /** Open claims touched ÷ open pile, pooled across the period. A scored line. */
  openWorkedPct: number | null
  /**
   * The two sums behind `openWorkedPct`: each month-end pile in the range, added up,
   * and how many of it were touched that month. NOT `openClaims`, which is the
   * worklist snapshot at export. Anything pooling open-worked across people divides
   * one sum by the other; weighting the rate by the snapshot is a different pile.
   */
  openPileSum: number
  openWorkedSum: number
  /**
   * The snapshot's pile by when anyone last touched it — four counts that add up to
   * `openClaims`. Null where the bundle predates them (`coverage.worklistAge`) or the
   * person has no worklist row.
   */
  age: WorklistAge | null

  facilities: number
  /** Only the months inside the selected range. */
  months: TouchMonthRow[]
  /**
   * How much of this person's WHOLE-PERIOD claims and labor falls inside the selected
   * range; 1 and 1 when no range is set. Scales the grains the bundle does not split
   * by month — the crosstab. See `derive`.
   */
  rangeShare: {
    claims: number
    labor: number
    /**
     * The same for the two kinds of labor apart: labor in the months that billed
     * claims, and labor in the months that billed none (payroll on no claim, §3.5).
     * The crosstab files the second in its own no-claim cells, so each of its rows takes
     * the share of its own kind — the overall `labor` share handed a facility a part of
     * the range's no-claim pay, and the no-claim bar a part of the claims' labor.
     */
    onClaims: number
    noClaim: number
  }

  stats: PersonStats
  score: Scorecard
  /**
   * Someone who has left, as they are RANKED: the same person rolled up over only the
   * months in the range they were active (`activeInMonth`). Absent when that is every
   * month they have — always, for anyone still on the books. Read it via `asRanked`.
   */
  rankedAs?: Person
}

/**
 * A person's pay and hours over the selected range — FactPayroll, so pay WITHOUT benefit
 * load (bonus in). A fact about the person, shown and never ranked: nothing here goes
 * into a cohort, a peer average or the scorecard ("let's not rank their labor",
 * 2026-09-23). Cost per claim keeps dividing `claimLabor`, the benefit-loaded figure.
 *
 * `worked` is every paid hour — the model's `Payroll Labor Hours`, PTO and OT
 * included; `ot` and `pto` are subsets of it (MEASURE-RULES §8.3).
 *
 * Null means the bundle does not carry the figure (built before 2026-09-30), or no
 * month in the range has a FactPayroll row — the page shows "—", never a zero.
 */
export interface PersonLabor {
  payroll: number | null
  worked: number | null
  ot: number | null
  pto: number | null
}

/**
 * `PersonLabor` for one person: their own row for the whole period, their month rows
 * summed for a range — the same split `claimLabor` uses.
 */
export function personLabor(
  r: EmployeeRow,
  months: readonly TouchMonthRow[],
  narrowed: boolean,
): PersonLabor {
  if (!narrowed)
    return {
      payroll: r.payroll,
      worked: r.laborHours ?? null,
      ot: r.otHours ?? null,
      pto: r.ptoHours ?? null,
    }
  const total = (get: (m: TouchMonthRow) => number | null | undefined) => {
    const got = months.map(get).filter((v): v is number => typeof v === 'number')
    return got.length ? got.reduce((a, b) => a + b, 0) : null
  }
  return {
    payroll: total((m) => m.payroll),
    worked: total((m) => m.laborHours),
    ot: total((m) => m.otHours),
    pto: total((m) => m.ptoHours),
  }
}

export interface Derived {
  people: Person[]
  byName: Map<string, Person>
  /**
   * The scorecard's peer population: every ACTIVE person's stats, less the low-volume
   * ones (review #695). Anything computing a peer average passes this, never all of
   * `people`. See `derive`, pass 2.
   */
  peerStats: PersonStats[]
  /** The months in the selected range — what every figure on screen covers. */
  months: string[]
  /** Every month the bundle carries, whatever the range. The picker's option list. */
  allMonths: string[]
  /** True when the range leaves out any month the bundle carries. */
  narrowed: boolean
  /**
   * The bill months every collection figure in this range is read over: start − 8 …
   * end − 3. Null only for a bundle with no months at all. See measures/collection.ts.
   */
  collectionWindow: CollectionWindow | null
  departments: string[]
  jobTitles: string[]
  supervisors: string[]
  vendors: string[]
  officeLocations: string[]
  /**
   * The claim-grain filter options, read off the composition rather than hardcoded.
   * A list typed into the UI is a list that silently drops a value the model starts
   * producing — the same class of bug as aggregating by a fixed department list.
   */
  claimTypes: string[]
  serviceTypes: string[]
  claimStatuses: string[]
  /** Shore values actually present. Never a hardcoded ['On','Off']. */
  shores: string[]
  facilityGroups: string[]
  states: string[]
  payers: string[]
}

/**
 * Distinct facilities per employee — the denominator behind the scorecard's
 * facilities line and `Average claims per facility`.
 *
 * `UNKNOWN` is excluded, per References/MEASURE-RULES.md §7.2: a claim allocation
 * whose facility does not resolve to a real facility is not a facility. Its rows
 * stay in the crosstab because the labor on them is real — $6.2M of it, the largest
 * unattributed bucket on the Overview page — but counting it would credit people
 * with a facility they do not work, and that count is worth +2 on the scorecard.
 *
 * This is also where the app deliberately departs from four Power BI reports, which
 * override the model measure with a bare DISTINCTCOUNT that includes UNKNOWN. Their
 * facility counts are overstated; the app is right. Tracked upstream as an Asana bug.
 */
function facilitiesByEmployee(d: Dataset): Map<string, Set<number>> {
  const out = new Map<string, Set<number>>()
  const names = d.crosstab.employees
  const unknown = new Set(
    d.crosstab.facilities
      // The no-claim bucket is not a facility either: it is payroll with no claim.
      .map((f, i) => (f.trim().toUpperCase() === 'UNKNOWN' || isNoClaim(f) ? i : -1))
      .filter((i) => i >= 0),
  )
  for (const row of d.crosstab.rows) {
    const name = names[row[0]]
    if (name === undefined) continue
    if (unknown.has(row[5])) continue
    let set = out.get(name)
    if (!set) out.set(name, (set = new Set()))
    set.add(row[5])
  }
  return out
}

/**
 * Every value any person's share map mentions, ordered for a checkbox list.
 *
 * Reading the options off the data rather than listing them in the UI is the same
 * rule the crosstab aggregation learned the hard way: a hardcoded five-department
 * list silently dropped 40,136 rows. 'Unknown' sorts last because it is a residual
 * bucket, not a peer of the real values.
 */
function keysAcross(
  rows: readonly CompositionRow[],
  ...picks: ((r: CompositionRow) => Record<string, number> | undefined)[]
): string[] {
  const seen = new Set<string>()
  for (const r of rows)
    for (const pick of picks) for (const k of Object.keys(pick(r) ?? {})) seen.add(k)
  const all = [...seen]
  const real = all.filter((x) => x !== 'Unknown').sort()
  return all.includes('Unknown') ? [...real, 'Unknown'] : real
}

/** The selected month window, inclusive, as 'YYYY-MM' keys. A null end is open. */
export interface MonthRange {
  from: string | null
  to: string | null
}

/**
 * Supervisors who only repeat someone below them: no row of their own in `people`, no
 * direct report in it, and everyone under them comes up through the same one person.
 * Their team is that person's team again. Under a department-scoped login (or View as)
 * that is every executive above the department — a card and a filter entry each, all
 * showing the scope once more with 0 direct reports. A manager of managers over two or
 * more teams in the data is not one of these and stays.
 */
export function chainOnlySupervisors(people: readonly Person[]): Set<string> {
  const inData = new Set(people.map((p) => p.employee))
  const via = new Map<string, string | null>()
  for (const p of people)
    p.supervisors.forEach((s, i) => {
      if (s === p.employee) return
      // The direct supervisor has a report here: never chain-only.
      const below = i === 0 ? null : (p.supervisors[i - 1] ?? null)
      const seen = via.get(s)
      via.set(s, seen === undefined ? below : seen === below ? seen : null)
    })
  const out = new Set<string>()
  for (const [s, below] of via) if (below !== null && !inData.has(s)) out.add(s)
  return out
}

/**
 * The per-person rollup over the months in `range` — every month when it is omitted.
 *
 * Every figure a person carries is rebuilt from their month rows inside the range, so
 * the date range reaches the headline cards, the tables, the cohorts and the
 * scorecard, not only the x-axis of the trend charts (which is all it used to move).
 * The month rows tie to the person rows exactly — claims and labor, all 744 people on
 * the 2026-09-23 bundle — so with no range this returns the figures it always did,
 * and under a range claims, labor, cost per claim, resolved claims, the touch shares
 * and open-worked % are exact.
 *
 * What the bundle does NOT carry by month, and what a range does to it:
 *
 *  - Collection %: not the range's months at all. It reads the range's lagged
 *    window — bill months start − 8 … end − 3 — pooled off the person's
 *    `collectionMonths` (MEASURE-RULES §4.3, measures/collection.ts). Always, with or
 *    without a range: the month rows carry overlapping windows and cannot be added.
 *    Null on a bundle without the series (built before 2026-10-05).
 *  - Facilities: a distinct count, which months cannot be added into. The busiest
 *    month in the range, capped at the period's count — a floor.
 *  - Touches per claim: all touches over billed claims, with no monthly form. Blank
 *    under a range rather than a whole-period figure passing as the range's.
 *  - The crosstab: scaled by `rangeShare`. Unattributed labor is NOT: it is the labor
 *    of the range's months that billed no claims, exact (MEASURE-RULES §3.5).
 *  - Payroll: `payroll` stays whole-period, as the payroll band already says. The
 *    person's labor line (`labor`: pay and hours) is rebuilt from the month rows.
 *  - The worklist: a snapshot at export, not a period measure at all.
 */
/**
 * The person's hire date. The bundle's own since 2026-10-09; on an older bundle, backed
 * out of `tenureYears`, which the builder counts from the hire date to the last
 * month's end — rounded to 0.01 years, so within about four days.
 */
export function hireDateOf(
  r: Pick<EmployeeRow, 'hireDate' | 'tenureYears'>,
  lastMonth: string | undefined,
): string | null {
  if (r.hireDate) return r.hireDate
  if (r.tenureYears === null || r.tenureYears === undefined || !lastMonth) return null
  const [y, m] = lastMonth.split('-').map(Number)
  const end = Date.UTC(y!, m!, 0)
  return new Date(end - r.tenureYears * 365.25 * 86_400_000).toISOString().slice(0, 10)
}

/**
 * How much of one whole-period crosstab row falls inside the range, for a person's
 * `rangeShare`: claims by their claims' share, labor by the share of its own kind — a
 * no-claim row (`isNoClaim` facility) the range's no-claim pay, any other row the labor
 * of the range's months that billed claims. 1 and 1 with no range.
 */
export const rowRangeShare = (
  share: Person['rangeShare'] | undefined,
  noClaimRow: boolean,
): { claims: number; labor: number } => ({
  claims: share?.claims ?? 1,
  labor: !share ? 1 : noClaimRow ? share.noClaim : share.onClaims,
})

export function derive(
  d: Dataset,
  range: MonthRange = { from: null, to: null },
  /**
   * Weighted claims: employee → month → Σ(weight − 1), from /api/weights. When given,
   * every claim-based figure counts weighted claims; closed % does not (see `Person`).
   * Null or omitted: standard claims, as always.
   */
  weightAdjustments: Readonly<Record<string, Readonly<Record<string, number>>>> | null = null,
): Derived {
  const folded = foldToPeople(d.employees)

  const allMonths = [...new Set(d.touchMonths.map((t) => t.month))].sort()
  const inRange = (m: string) =>
    (range.from === null || m >= range.from) && (range.to === null || m <= range.to)
  const months = allMonths.filter(inRange)
  const narrowed = months.length < allMonths.length
  // The bill months collection is read over for this range. See measures/collection.ts.
  const first = months[0] ?? allMonths[0]
  const last = months[months.length - 1] ?? allMonths[allMonths.length - 1]
  const collWindow = first && last ? collectionWindow(first, last) : null

  const monthsBy = new Map<string, TouchMonthRow[]>()
  for (const row of d.touchMonths) {
    if (!inRange(row.month)) continue
    // Weighted: a copy carrying the month's weighted count, for the trend. The bundle's
    // own rows are never written to.
    const adj = weightAdjustments?.[row.employee]?.[row.month]
    const t = weightAdjustments ? { ...row, weightedClaims: row.billedClaims + (adj ?? 0) } : row
    const list = monthsBy.get(t.employee)
    if (list) list.push(t)
    else monthsBy.set(t.employee, [t])
  }

  const worklistBy = new Map<string, WorklistRow>(d.worklist.map((w) => [w.employee, w]))
  const facBy = facilitiesByEmployee(d)

  // Pass 1 — per-person facts that need no peer context. `rebuild` takes every
  // figure off the month rows given, as a range does; the whole period reads the row.
  const rollup = (r: EmployeeRow, months: TouchMonthRow[], rebuild: boolean) => {
    const resolved = months.reduce((a, m) => a + m.resolvedClaims, 0)
    const noTouch = months.reduce((a, m) => a + m.resolvedNoTouch, 0)
    const touched = months.reduce((a, m) => a + touchBase(m), 0)
    const w = worklistBy.get(r.employee)
    const periodFacilities = facBy.get(r.employee)?.size ?? 0

    // Whole-period figures come off the person row, as they always have; a range
    // rebuilds them from the month rows. See the note above `derive`.
    let claims = r.claims
    let claimLabor = r.claimLabor
    // The months in hand that billed no claims — the whole period's are the person
    // row's (`hydrate`), a range's are its own. Exact either way; never scaled.
    const unattributedLabor = months.reduce(
      (a, m) => (isNoClaimMonth(m) ? a + m.claimLabor : a),
      0,
    )
    const collection =
      first && last ? windowCollection(r.collectionMonths, first, last) : null
    const collectedPct = collectionRateOf(collection)
    let touchesPerClaim = r.touchesPerClaim
    let facilities = periodFacilities
    let rangeShare = { claims: 1, labor: 1, onClaims: 1, noClaim: 1 }
    if (rebuild) {
      claims = months.reduce((a, m) => a + m.billedClaims, 0)
      claimLabor = months.reduce((a, m) => a + m.claimLabor, 0)
      const labor = r.claimLabor === 0 ? 0 : claimLabor / r.claimLabor
      // A kind the whole period has none of falls back to the overall share.
      const part = (inRange: number, whole: number) => (whole === 0 ? labor : inRange / whole)
      rangeShare = {
        claims: r.claims === 0 ? 0 : claims / r.claims,
        labor,
        onClaims: part(claimLabor - unattributedLabor, r.claimLabor - r.unattributedLabor),
        noClaim: part(unattributedLabor, r.unattributedLabor),
      }
      touchesPerClaim = null
      facilities = Math.min(
        periodFacilities,
        months.reduce((a, m) => Math.max(a, m.facilities), 0),
      )
    }

    // Weighted claims: the standard count plus the rules' adjustment for each month of
    // the range. Everything that divides by claims divides by `counted`; closed % stays
    // on billed claims, because its numerator (resolved claims) is not weighted and the
    // two bases must never share a ratio (MEASURE-RULES §1.1).
    const standardClaims = claims
    const adj = weightAdjustments ? (weightAdjustments[r.employee] ?? {}) : null
    const weightedClaims = adj ? months.reduce((a, m) => a + (adj[m.month] ?? 0), claims) : null
    const counted = weightedClaims ?? claims
    // The crosstab is whole-period: under a range its rows are scaled by this share.
    // Weighted, the rows are weighted cells (data/weightedCrosstab.ts), so the share
    // is of weighted claims too — then the breakdowns add up to the weighted cards.
    if (adj && rebuild) {
      const whole = Object.values(adj).reduce((a, v) => a + v, r.claims)
      rangeShare = { ...rangeShare, claims: whole === 0 ? 0 : counted / whole }
    }

    // Open claims worked, over the whole range rather than one month: summing both
    // sides and dividing once weights each month by the size of the pile that month.
    // Averaging the monthly rates instead would give a light month with 4 open claims
    // the same say as a heavy one with 900.
    const openSum = months.reduce((a, m) => a + (m.openClaims ?? 0), 0)
    const openWorkedSum = months.reduce((a, m) => a + (m.openClaimsWorked ?? 0), 0)
    const zeroTouchShare = divide(noTouch, touched)
    const openWorkedPct = divide(openWorkedSum, openSum)

    const stats: PersonStats = {
      employee: r.employee,
      department: r.department,
      jobTitle: r.jobTitle,
      shore: r.shore,
      claims: counted,
      costPerClaim: divide(claimLabor, counted),
      // UNCAPPED — see measures/core.ts. Backlog clearers exceed 100%.
      closedClaimPct: divide(resolved, standardClaims),
      facilities,
      collectedPct,
      zeroTouchShare,
      openWorkedPct,
    }

    return {
      employee: r.employee,
      supervisor: r.supervisor,
      supervisors: r.supervisors ?? (r.supervisor ? [r.supervisor] : []),
      supervisorTitles: r.supervisorTitles ?? [],
      supervisorOutsideScope: r.supervisorOutsideScope === true,
      department: r.department,
      jobTitle: r.jobTitle,
      shore: r.shore,
      paylocityId: r.paylocityId ?? null,
      vendor: r.vendor ?? 'Direct',
      officeLocation: r.officeLocation ?? 'Unknown',
      management: r.management,
      terminated: r.terminationDate !== null,
      terminationDate: r.terminationDate,
      tenureYears: r.tenureYears,
      hireDate: hireDateOf(r, allMonths[allMonths.length - 1]),

      claims: counted,
      standardClaims,
      weightedClaims,
      claimLabor,
      unattributedLabor,
      payroll: r.payroll,
      labor: personLabor(r, months, rebuild),
      collectedPct,
      collection,
      collectionMonths: r.collectionMonths,

      costPerClaim: stats.costPerClaim,
      resolvedClaims: resolved,
      closedClaimPct: stats.closedClaimPct,
      noTouch,
      touchBase: touched,
      zeroTouchShare,
      touchesPerClaim,
      // Per month OF THE RANGE; eight for the full period, as before.
      billedPerMonth: divide(counted, months.length || allMonths.length),

      openClaims: w?.openClaims ?? 0,
      notWorkedThisMonth: w?.claimsNotWorked ?? 0,
      openWorkedPct,
      openPileSum: openSum,
      openWorkedSum,
      age: worklistAge(w),

      facilities,
      months,
      rangeShare,
      stats,
    }
  }
  type Base = ReturnType<typeof rollup> & { rankedAs?: ReturnType<typeof rollup> }
  const base = folded.map((r: EmployeeRow): Base => {
    const months = (monthsBy.get(r.employee) ?? []).sort((a, b) =>
      a.month < b.month ? -1 : 1,
    )
    const own = rollup(r, months, narrowed)
    // Someone who has left is ranked on the months they were active only (review #61,
    // 2026-10-05). A month row after the termination month is a final paycheck or a
    // late resolution, and its resolutions would put their billed-vs-resolved % far
    // over 100%. Shown figures keep every row; see `asRanked`.
    const active = months.filter((m) => activeInMonth(r, m.month))
    return active.length === months.length ? own : { ...own, rankedAs: rollup(r, active, true) }
  })

  // Pass 2 — the scorecard needs every peer's stats to compute peer averages. Peers are
  // ACTIVE staff only, and a terminated person is not scored: "ranking should
  // obviously only be on active employees" (Shayna, 2026-09-25). Until then the peer
  // averages took in everyone who had left. See DECISION-LOG.md §8.
  //
  // Minimum billing volume (review #695 as Shayna rewrote it 2026-10-18;
  // measures/minimumVolume.ts): each job title's minimum per month, from the bottom
  // quarter of its active staff in full months; each person tested in their own FULL
  // months only (Yehuda, 2026-10-19: a hire, leaving, open or short month is not tested);
  // those under it scored 0 and left out of everyone's peer averages, as terminated staff
  // are. One pass: leaving them out re-tests nobody.
  const open = new Set(d.openMonths)
  const hoursBy = new Map<string, TouchMonthRow[]>()
  for (const r of d.touchMonths) {
    if (open.has(r.month)) continue
    const list = hoursBy.get(r.employee)
    if (list) list.push(r)
    else hoursBy.set(r.employee, [r])
  }
  const withUsual = base.map((p) => ({ ...p, usual: usualHours(hoursBy.get(p.employee) ?? []) }))
  const bars = volumeBars(withUsual, open)
  // Each person's full months: the only months they are tested in.
  const fullOf = new Map(withUsual.map((p) => [p.stats, p.months.filter((r) => fullMonth(r, p, open))] as const))
  const barsOf = (p: { jobTitle: string; stats: PersonStats }) => {
    const out: Record<string, VolumeBar> = {}
    for (const r of fullOf.get(p.stats) ?? []) {
      const b = bars.get(`${p.jobTitle}|${r.month}`)
      if (b) out[r.month] = b
    }
    return out
  }
  // Billed claims over the full months against the sum of their minimums; no full month
  // with a minimum, no test.
  const volumeOf = new Map(
    base.map((p) => [p.stats, rangeVolume(fullOf.get(p.stats) ?? [], (m) => bars.get(`${p.jobTitle}|${m}`))] as const),
  )
  const active = base.filter((p) => !p.terminated).map((p) => p.stats)
  const peerStats = active.filter((s) => {
    const v = volumeOf.get(s)
    return !(s.claims > 0 && v && isLowVolume(v.claims, v.bar))
  })
  const people: Person[] = base.map(({ rankedAs, ...p }) => {
    const score = scorecard(peerStats, p.stats, volumeOf.get(p.stats) ?? null)
    const unscored = { ...score, scored: false, terminated: true }
    return {
      ...p,
      volumeBars: barsOf(p),
      score: p.terminated ? unscored : score,
      ...(rankedAs ? { rankedAs: { ...rankedAs, score: unscored } } : {}),
    }
  })

  const uniq = (xs: (string | null)[]) =>
    [...new Set(xs.filter((x): x is string => x !== null && x !== ''))].sort()

  return {
    people,
    byName: new Map(people.map((p) => [p.employee, p])),
    peerStats,
    months,
    allMonths,
    narrowed,
    collectionWindow: collWindow,
    departments: uniq(people.map((p) => p.department)),
    jobTitles: uniq(people.map((p) => p.jobTitle)),
    // Everyone anybody reports to at any level, so a manager of managers can be
    // picked even when nobody in this data reports to them directly — less the names
    // that only repeat someone below them (`chainOnlySupervisors`).
    supervisors: (() => {
      const skip = chainOnlySupervisors(people)
      return uniq(people.flatMap((p) => p.supervisors)).filter((s) => !skip.has(s))
    })(),
    vendors: uniq(people.map((p) => p.vendor)),
    officeLocations: uniq(people.map((p) => p.officeLocation)),
    // Union of the claim and labor maps, not just the claim one. They differ, and the
    // difference is the whole unattributed pile: claim type 'Other' and claim status
    // 'Unknown' carry $6.4M of labor on ZERO claims across 374 people, so a list built
    // from claim shares alone hides the largest thing in the data behind a filter that
    // does not offer it.
    claimTypes: keysAcross(
      d.composition,
      (c) => c.byClaimType,
      (c) => c.laborByClaimType,
    ),
    serviceTypes: keysAcross(
      d.composition,
      (c) => c.byServiceType,
      (c) => c.laborByServiceType,
    ),
    claimStatuses: keysAcross(
      d.composition,
      (c) => c.byStatus,
      (c) => c.laborByStatus,
    ),
    shores: uniq(people.map((p) => p.shore)),
    // The slicer's narrowing lists. The no-claim bucket is a bar, not something to
    // narrow to: it has no state, payer or facility group for another bar to share.
    facilityGroups: d.crosstab.facilityGroups.filter((v) => !isNoClaim(v)).sort(),
    states: d.crosstab.states.filter((v) => !isNoClaim(v)).sort(),
    payers: d.crosstab.payers.filter((v) => !isNoClaim(v)).sort(),
  }
}

/**
 * The trailing score for everybody: `scorecard()` on the last TRAILING_MONTHS closed
 * months ending at the range's end (measures/trailing.ts, MEASURE-RULES §10.8).
 *
 * It is a whole `derive` over the trailing window — the only way to get every person's
 * figures pooled over those months and every peer average over the people active in
 * them — so callers memoize it on the window, not on the range: moving the range's
 * start never recomputes it. When the window IS the range, `reuse` hands back the
 * range's own scores rather than deriving the same months twice.
 */
export interface TrailingScores {
  /** The months the trailing score covers. Empty when the bundle has no closed month. */
  months: string[]
  /** False when fewer closed months exist than the window asks for. */
  complete: boolean
  byName: Map<string, Scorecard>
}

export function trailingScores(
  d: Dataset,
  window: readonly string[],
  wanted: number,
  /** Must have been derived with the same `weightAdjustments`. */
  reuse?: Derived,
  /** The Weighted claims switch's adjustments, as `derive` takes them. */
  weightAdjustments: Parameters<typeof derive>[2] = null,
): TrailingScores {
  const months = [...window]
  const complete = months.length >= wanted
  if (months.length === 0) return { months, complete, byName: new Map() }
  const same =
    reuse !== undefined &&
    reuse.months.length === months.length &&
    reuse.months.every((m, i) => m === months[i])
  const from = same ? reuse : derive(d, { from: months[0]!, to: months[months.length - 1]! }, weightAdjustments)
  return { months, complete, byName: new Map(from.people.map((p) => [p.employee, p.score])) }
}

/* ------------------------------------------------------------------- ranking */

/**
 * Whether a person was active in a month ('YYYY-MM'): not terminated before it began.
 * Someone who leaves on the 18th was active that month (Shayna, review #61, 2026-10-05:
 * rank people "for all months they were active"). MEASURE-RULES §10.7.
 */
export const activeInMonth = (p: Pick<Person, 'terminationDate'>, month: string) =>
  p.terminationDate === null || p.terminationDate >= `${month}-01`

/**
 * Whether a person was active in at least one month of the range — who a PERIOD rank
 * takes in. Anyone still on the books is; someone who has left is when one of their
 * month rows in the range falls on or before their termination month.
 */
export const activeInRange = (p: Pick<Person, 'terminated' | 'terminationDate' | 'months'>) =>
  !p.terminated || p.months.some((m) => activeInMonth(p, m.month))

/**
 * The person as they are ranked: over the months they were active. The shown person
 * for anyone still on the books; see `Person.rankedAs`.
 */
export const asRanked = (p: Person): Person => p.rankedAs ?? p

/**
 * Whether a person belongs in a ranking pool for the period: active in it (above), and
 * carrying claims or with a month row in it (on the books that period, claims or not).
 * Someone hired after the range, or gone before it, is nobody's peer that period.
 */
export const inRankPeriod = (
  p: Pick<Person, 'claims' | 'months' | 'terminated' | 'terminationDate'>,
) => activeInRange(p) && (p.claims > 0 || p.months.length > 0)

/**
 * Job-title cohorts: every person passed in who was active in the period, grouped by
 * title — claims or not, still on the books or since left. A person with nothing to
 * rank on a measure is still in their title's pool, at the bottom, as "no data"
 * (Shayna, 2026-10-02; MEASURE-RULES §10.7). Peer averages and the colour bands stay on
 * the people with a value.
 *
 * Someone who has left is in the pool for the months they were active, and is entered
 * as they are ranked — rolled up over those months (`asRanked`, review #61,
 * 2026-10-05). Until then nobody who had left was ranked at all (Rule 10.6, 2026-09-25).
 *
 * Built per filter state by `applyFilters`, not once per bundle, because a person's
 * figure and their peers' figures must come off the same claim-filter reweight. See
 * `Scope.cohorts` for who is passed in.
 */
export function buildCohorts(people: readonly Person[]): Map<string, Person[]> {
  const cohorts = new Map<string, Person[]>()
  for (const p of people) {
    if (!inRankPeriod(p)) continue
    const list = cohorts.get(p.jobTitle)
    if (list) list.push(asRanked(p))
    else cohorts.set(p.jobTitle, [asRanked(p)])
  }
  return cohorts
}

export type MetricKey =
  | 'costPerClaim'
  | 'billedPerMonth'
  | 'collectedPct'
  | 'closedClaimPct'
  | 'zeroTouchShare'
  | 'openWorkedPct'
  | 'facilities'
  | 'claims'
  | 'touchesPerClaim'
  | 'openClaims'

export interface MetricDef {
  key: MetricKey
  label: string
  short: string
  /** Lower is better for cost, touches and the open pile. */
  lowerIsBetter: boolean
  /** Whether the scorecard awards points for it. */
  scored: boolean
  format: 'money2' | 'money0' | 'pct' | 'int' | 'dec'
  get: (p: Person) => number | null
  /**
   * The bundle grain this metric needs. A metric whose grain is absent is dropped from
   * every ranking surface rather than printed as 509 identical dashes — a column of
   * "— / no cohort" costs a reader the same attention as a column of numbers and
   * repays none of it. See data/coverage.ts.
   */
  needs?: GrainKey
}

export const METRICS: MetricDef[] = [
  /*
   * Ordered as Shayna's artifact orders them, and the ordering is a decision:
   * an unscored CONTEXT metric sits beside the scored one it explains rather than
   * being banished to the right. Billed total belongs next to billed-per-month;
   * touches-per-claim next to 0% touch share; the open pile next to how much of it
   * was worked. Reading across a row should follow the argument.
   */
  {
    key: 'costPerClaim',
    label: 'Cost per claim',
    short: 'Cost / claim',
    lowerIsBetter: true,
    scored: true,
    format: 'money2',
    get: (p) => p.costPerClaim,
  },
  {
    key: 'billedPerMonth',
    label: 'Billed per month',
    short: 'Billed / month',
    lowerIsBetter: false,
    scored: true,
    format: 'int',
    get: (p) => p.billedPerMonth,
  },
  {
    key: 'claims',
    label: 'Billed total',
    short: 'Billed total',
    lowerIsBetter: false,
    scored: false,
    format: 'int',
    get: (p) => p.claims,
  },
  {
    key: 'collectedPct',
    label: 'Collection',
    short: 'Collection',
    lowerIsBetter: false,
    scored: true,
    format: 'pct',
    get: (p) => p.collectedPct,
    needs: 'collectedPct',
  },
  {
    key: 'closedClaimPct',
    // Renamed 2026-09-22 to the scorecard's own vocabulary. Same measure — resolved
    // over billed, uncapped — and still the only line carrying a penalty.
    label: 'Claims billed vs resolved %',
    short: 'Billed vs resolved %',
    lowerIsBetter: false,
    scored: true,
    format: 'pct',
    get: (p) => p.closedClaimPct,
  },
  {
    key: 'zeroTouchShare',
    label: '0% touch',
    short: '0% touch',
    lowerIsBetter: false,
    scored: true,
    format: 'pct',
    get: (p) => p.zeroTouchShare,
  },
  {
    key: 'touchesPerClaim',
    label: 'Touches / claim',
    short: 'Touches / claim',
    lowerIsBetter: true,
    scored: false,
    format: 'dec',
    get: (p) => p.touchesPerClaim,
  },
  {
    key: 'openWorkedPct',
    // Was "Worked 30d", which the pipeline never populated and which named a window
    // the data does not have. This is the monthly rate the 11-point scorecard scores:
    // of the claims open to this person, how many they touched.
    label: 'Open claims worked in month %',
    short: 'Worked in month',
    lowerIsBetter: false,
    scored: true,
    format: 'pct',
    get: (p) => p.openWorkedPct,
    needs: 'worklist',
  },
  {
    key: 'facilities',
    label: 'Facilities',
    short: 'Facilities handled',
    lowerIsBetter: false,
    scored: true,
    format: 'int',
    get: (p) => p.facilities,
  },
  {
    key: 'openClaims',
    label: 'Open',
    short: 'Open',
    lowerIsBetter: true,
    scored: false,
    format: 'int',
    get: (p) => p.openClaims,
    needs: 'worklist',
  },
]

export const METRIC_BY_KEY = new Map(METRICS.map((m) => [m.key, m]))

/** The metrics this bundle can actually populate. */
export const availableMetrics = (coverage: Coverage): MetricDef[] =>
  METRICS.filter((m) => m.needs === undefined || coverage[m.needs])

/** The ones it cannot, so a page can name them instead of silently dropping them. */
export const absentMetrics = (coverage: Coverage): MetricDef[] =>
  METRICS.filter((m) => m.needs !== undefined && !coverage[m.needs])

/**
 * `RankFacts` carries the rank, the pool (`of`, no-data included), how many in it have
 * no data, and the group's name — everything `rankText` needs to say it in words.
 */
export interface Standing extends RankFacts {
  value: number | null
  /** Position in cohort as a share, 0 = best. Null when there is no cohort. */
  pctile: number | null
  median: number | null
  /** The cohort's value range, so a reader can see spread rather than only rank. */
  min: number | null
  max: number | null
  /** 'good' = top third of the cohort, 'crit' = bottom third, 'mid' otherwise. */
  band: 'good' | 'mid' | 'crit' | null
  /**
   * The person had left before every month of the range, so is not ranked, whatever the
   * cohort. Someone who left during the range is ranked on the months they were active.
   */
  terminated: boolean
}

/**
 * Shore and vendor as the small suffix after a person's title in the people lists —
 * " · Offshore · <vendor>", blanks skipped ("put near the employee name offshore/onshore
 * and vendor in small", 2026-10-01). 'Direct' (FCC payroll) is not a vendor and is left
 * off, so only outside vendors show. Empty when neither is shown.
 */
export function shoreVendor(p: Pick<Person, 'shore' | 'vendor'>): string {
  const shore = p.shore === 'Off' ? 'Offshore' : p.shore === 'On' ? 'Onshore' : null
  const vendor = p.vendor?.trim()
  return [shore, vendor && vendor !== 'Direct' ? vendor : null]
    .filter((x): x is string => !!x)
    .map((x) => ` · ${x}`)
    .join('')
}

/**
 * The Employees page's line under a person's name: title, how many hold it, shore and
 * vendor. `holders` is the size of their job-title cohort. Shared with "My people" so
 * the two lists describe a person the same way (Shayna, review #107, 2026-10-06).
 */
export function personSubLine(
  p: Pick<Person, 'jobTitle' | 'shore' | 'vendor'>,
  holders: number,
): string {
  return `${p.jobTitle} · ${holders >= MIN_COHORT ? `${int(holders)} hold this title` : 'no cohort'}${shoreVendor(p)}`
}

/** What a rank cell says on hover for somebody who has left. One wording everywhere. */
export { NOT_RANKED_TERMINATED } from '@/measures/rankText'

const median = (xs: number[]): number | null => {
  if (!xs.length) return null
  const s = [...xs].sort((a, b) => a - b)
  const m = s.length >> 1
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2
}

/**
 * The figure a person is RANKED on: the metric, or null — "no data" — when they carry
 * no claims in the period. A zero-claim person reads 0 on the billed metrics, not null;
 * as a value it would pull the title's median and bands down and earn a real last
 * place. In the pool, at the bottom, as no data, never in the comparison (§10.7).
 */
const rankedValue =
  (metric: Pick<MetricDef, 'get'>) =>
  (p: Person): number | null =>
    p.claims > 0 ? metric.get(p) : null

/**
 * Where this person stands in their own job title on one metric.
 *
 * The pool (`of`) is everyone in the title active in the period, data or not. Those with no value on this metric are counted (`noData`), placed at the
 * bottom and shown as "no data" — never a real last place. Rank, percentile, median
 * and range are taken over the people WITH a value; fewer than five of those returns
 * `rank: null` — "no cohort", never a number computed off two people. MEASURE-RULES
 * §10.7.
 *
 * Someone who has left is ranked on the months of the range they were active, on the
 * figure for those months (`asRanked`; review #61, 2026-10-05). Someone who had left
 * before the range gets `rank: null` and `terminated: true` however big the cohort:
 * listed, not ranked. The median and range still describe their peers.
 */
export function standing(
  cohorts: Map<string, Person[]>,
  person: Person,
  metric: Pick<MetricDef, 'get' | 'lowerIsBetter'>,
): Standing {
  return standIn(cohorts.get(person.jobTitle) ?? [], person, metric, !activeInRange(person))
}

function standIn(
  cohort: readonly Person[],
  person: Person,
  metric: Pick<MetricDef, 'get' | 'lowerIsBetter'>,
  notRanked: boolean,
): Standing {
  /** The figure shown — whatever the metric says, a zero included. */
  const value = metric.get(person)
  const ranked = rankedValue(metric)
  const mine = ranked(asRanked(person))
  const vals = cohort.map(ranked).filter((v): v is number => v !== null)
  const med = median(vals)
  const min = vals.length ? Math.min(...vals) : null
  const max = vals.length ? Math.max(...vals) : null
  const facts = {
    of: cohort.length,
    noData: cohort.length - vals.length,
    hasValue: mine !== null,
    floor: MIN_COHORT,
    group: titlePlural(person.jobTitle),
    terminated: notRanked,
  }

  if (vals.length < MIN_COHORT || mine === null || notRanked) {
    return { ...facts, value, rank: null, pctile: null, median: med, min, max, band: null }
  }

  const better = new Set(vals.filter((v) => (metric.lowerIsBetter ? v < mine : v > mine)))
  const rank = better.size + 1
  const pctile = vals.length > 1 ? (rank - 1) / (vals.length - 1) : 0

  // Even thirds, one colour each way. No amber band.
  const band = pctile <= 1 / 3 ? 'good' : pctile >= 2 / 3 ? 'crit' : 'mid'

  return { ...facts, value, rank, pctile, median: med, min, max, band }
}

/**
 * The plain mean of a metric over the person's job-title pool — the people `standing`
 * ranks them among who have a value (MEASURE-RULES §10.7), the person included. Null
 * under the cohort floor, where `standing` gives no rank either: an average of two
 * people is not a peer average. Plain mean, not claim-weighted, as the scorecard's.
 */
export function peerMean(
  cohorts: Map<string, Person[]>,
  person: Person,
  metric: Pick<MetricDef, 'get'>,
): number | null {
  const ranked = rankedValue(metric)
  const vals = (cohorts.get(person.jobTitle) ?? [])
    .map(ranked)
    .filter((v): v is number => v !== null)
  return vals.length < MIN_COHORT ? null : vals.reduce((a, b) => a + b, 0) / vals.length
}

/**
 * Place by scorecard total within the person's own job title — the same pool as every
 * other rank (MEASURE-RULES §10.7), less anyone who has left: nobody who has left is
 * scored (Rule 10.6, DECISION-LOG §8b), so they would only pad the pool with "no data".
 * Anyone else in the title who is not scored has no data and sits at the bottom. The
 * Scorecard's Rank column, the person hero and the monthly statements all read it.
 *
 * TIES (since 2026-10-21, monthly scorecard statements): equal scores are broken by the
 * trailing 3-month score, then by cost per claim (lower first), when `trailing` is given.
 * People still level on all three share a rank, and the next rank follows without a gap
 * (1, 1, 2), as every rank does. Someone with no trailing score or no cost per claim
 * sorts after those with one on that step. Without `trailing`, score ties share a rank.
 */
export function scoreStanding(
  cohorts: Map<string, Person[]>,
  person: Person,
  trailing?: ReadonlyMap<string, Pick<Scorecard, 'scored' | 'total'>>,
): Standing {
  const cohort = (cohorts.get(person.jobTitle) ?? []).filter((p) => !p.terminated)
  const metric = { get: (p: Person) => (p.score.scored ? p.score.total : null), lowerIsBetter: false }
  const base = standIn(cohort, person, metric, person.terminated)
  if (!trailing || base.rank === null) return base

  const ranked = rankedValue(metric)
  // Higher is better on every element: the score, the trailing score, minus cost per claim.
  const key = (p: Person): [number, number, number] => {
    const t = trailing.get(p.employee)
    return [
      ranked(p)!,
      t?.scored ? t.total : -Infinity,
      p.costPerClaim !== null && Number.isFinite(p.costPerClaim) ? -p.costPerClaim : -Infinity,
    ]
  }
  const ahead = (a: readonly number[], b: readonly number[]) => {
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i]! > b[i]!
    return false
  }
  const mine = key(asRanked(person))
  const better = new Set(
    cohort
      .filter((p) => ranked(p) !== null)
      .map(key)
      .filter((k) => ahead(k, mine))
      .map((k) => k.join('|')),
  )
  const compared = cohort.length - base.noData
  const rank = better.size + 1
  const pctile = compared > 1 ? Math.min(1, (rank - 1) / (compared - 1)) : 0
  const band = pctile <= 1 / 3 ? 'good' : pctile >= 2 / 3 ? 'crit' : 'mid'
  return { ...base, rank, pctile, band }
}
